import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  Card,
  Dialog,
  EmptyState,
  Filters,
  Input,
  LoadingState,
  Search,
  SectionHeader,
  Select,
  StatusChip,
  type StatusTone,
  Table,
  Tabs,
  Tbody,
  Td,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { ApiError, apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import {
  createArticlesApi,
  type ArticleAction,
  type ArticleAudience,
  type ArticleDetail,
  type ArticleDto,
  type ArticleListParams,
  type ArticleStatus,
  type ArticleType,
  type KnowledgeSummary,
} from "../articles-api";
import { formatLabel } from "../labels";
import { Markdown } from "../Markdown";

const TYPE_LABEL: Record<ArticleType, string> = {
  help_article: "Help article (users)",
  playbook: "Playbook",
  sop: "SOP",
  template: "Reply template / macro",
  policy: "Policy explainer",
  training: "Training",
  emergency_procedure: "Emergency procedure",
};

export function statusTone(s: ArticleStatus): StatusTone {
  if (s === "published") return "success";
  if (s === "under_review") return "warning";
  if (s === "approved") return "info";
  if (s === "draft") return "neutral";
  return "neutral";
}

const ACTION_LABEL: Record<ArticleAction, string> = {
  submit_review: "Send for review",
  request_changes: "Send back",
  approve: "Approve",
  publish: "Publish",
  unpublish: "Unpublish",
  supersede: "Mark superseded",
  archive: "Archive",
  restore: "Restore to draft",
};

const NOTE_REQUIRED: ArticleAction[] = ["request_changes", "unpublish"];
const NOTE_OPTIONAL: ArticleAction[] = ["archive", "supersede", "approve"];

type View = "public" | "internal" | "review" | "archived";
const VIEWS: { id: View; label: string; params: ArticleListParams; empty: string }[] = [
  {
    id: "public",
    label: "Help center",
    params: { audience: "public", status: "draft,under_review,approved,published" },
    empty: "No help articles yet. Write one and publish it, and Play users can read it under Profile › Help.",
  },
  {
    id: "internal",
    label: "Internal playbooks",
    params: { audience: "internal", status: "draft,under_review,approved,published" },
    empty: "No internal playbooks yet. Write the steps your support team follows, and reply templates they can copy.",
  },
  { id: "review", label: "Needs review", params: { status: "under_review,approved" }, empty: "Nothing is waiting for review." },
  { id: "archived", label: "Archived", params: { status: "archived,superseded" }, empty: "Nothing archived." },
];

const PAGE = 25;

export function KnowledgeHubPage() {
  usePageBreadcrumbs([{ label: "HUDDL HQ", to: "/app" }, { label: "Knowledge Base" }]);
  return <ArticleCenter />;
}

export function ArticlesQueuePage() {
  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Knowledge Base", to: "/app/knowledge" },
    { label: "Articles" },
  ]);
  return <ArticleCenter />;
}

function ArticleCenter() {
  const navigate = useNavigate();
  const toast = useToast();
  const client = useApiClient();
  const api = useMemo(() => createArticlesApi(client), [client]);
  const [params, setParams] = useSearchParams();
  const view = (params.get("view") as View | null) ?? "public";
  const q = params.get("q") ?? "";
  const category = params.get("category") ?? "";
  const overdue = params.get("review_overdue") === "true";
  const [search, setSearch] = useState(q);
  const [rows, setRows] = useState<ArticleDto[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [next, setNext] = useState<string | null>(null);
  const [summary, setSummary] = useState<KnowledgeSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  function update(patch: Record<string, string>) {
    const n = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) n.set(k, v);
      else n.delete(k);
    }
    setParams(n);
    setCursors([undefined]);
  }

  useEffect(() => {
    const t = setTimeout(() => {
      if (search !== q) update({ q: search.trim() });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const cursor = cursors[cursors.length - 1];
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const base = VIEWS.find((v) => v.id === view)!.params;
      const [list, sum] = await Promise.all([
        api.list({
          ...base,
          limit: PAGE,
          ...(cursor ? { cursor } : {}),
          ...(q ? { q } : {}),
          ...(category ? { category } : {}),
          ...(overdue ? { review_overdue: true } : {}),
        }),
        api.summary(),
      ]);
      setRows(list.items);
      setNext(list.next_cursor);
      setTotal(list.meta.total ?? null);
      setSummary(sum);
      setForbidden(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setForbidden(true);
      setError(apiErrorMessage(err));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [api, view, cursor, q, category, overdue]);

  useEffect(() => {
    void load();
  }, [load]);

  if (forbidden) {
    return (
      <Stack gap={24}>
        <SectionHeader title="Knowledge Base" />
        <EmptyState
          title="You don't have access to the Knowledge Base"
          description="Reading needs Operations › View or Platform › View. Writing needs Operations › Manage; publishing needs Platform › Configure. A Super Admin can grant these under System › Access."
          action={<Button variant="secondary" onClick={() => void navigate("/app/system/access")}>Open Access</Button>}
        />
      </Stack>
    );
  }

  const filtered = !!(q || category || overdue);
  const table = (
    <Stack gap={12}>
      {loading ? <LoadingState message="Loading articles…" /> : null}
      {!loading && error ? (
        <Alert tone="danger" title="Could not load articles">
          {error}{" "}
          <Button size="sm" variant="ghost" onClick={() => void load()}>Retry</Button>
        </Alert>
      ) : null}
      {!loading && !error && rows.length === 0 ? (
        <EmptyState
          title={filtered ? "No articles match" : "Nothing here yet"}
          description={filtered ? "Clear the search or filters." : VIEWS.find((v) => v.id === view)?.empty}
          action={
            filtered ? (
              <Button variant="secondary" onClick={() => { setSearch(""); update({ q: "", category: "", review_overdue: "" }); }}>Clear</Button>
            ) : view === "public" || view === "internal" ? (
              <Button onClick={() => setCreateOpen(true)}>Write an article</Button>
            ) : undefined
          }
        />
      ) : null}
      {!loading && rows.length > 0 ? (
        <Table>
          <Thead>
            <Tr>
              <Th>Article</Th>
              <Th>Status</Th>
              <Th>Category</Th>
              <Th>Readers</Th>
              <Th>Updated</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((a) => (
              <Tr key={a.id} style={{ cursor: "pointer" }} onClick={() => void navigate(`/app/knowledge/articles/${a.id}`)}>
                <Td>
                  <Stack gap={4}>
                    <Text role="label" as="span">{a.title ?? "Untitled"}</Text>
                    <Text role="caption" color="secondary" as="span">
                      {TYPE_LABEL[a.item_type]}
                      {a.summary ? ` · ${a.summary}` : ""}
                    </Text>
                  </Stack>
                </Td>
                <Td>
                  <Flex gap={4} wrap>
                    <StatusChip tone={statusTone(a.status)}>{formatLabel(a.status)}</StatusChip>
                    {a.review_overdue ? <StatusChip tone="danger">Review due</StatusChip> : null}
                  </Flex>
                </Td>
                <Td>{a.category ?? "—"}</Td>
                <Td>
                  {a.audience === "public" ? (
                    <Text role="small" as="span">
                      {a.view_count} views · {a.helpful}👍 {a.not_helpful}👎
                    </Text>
                  ) : (
                    <Text role="small" color="muted" as="span">Staff only</Text>
                  )}
                </Td>
                <Td>{new Date(a.updated_at).toLocaleDateString()}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      ) : null}
      {!loading && rows.length > 0 ? (
        <ActionBar
          left={<Text role="small" color="secondary" as="span">{total !== null ? `${total} article${total === 1 ? "" : "s"}` : ""}</Text>}
          right={
            <Flex gap={8}>
              <Button size="sm" variant="secondary" disabled={cursors.length <= 1} onClick={() => setCursors((c) => c.slice(0, -1))}>Previous</Button>
              <Button size="sm" variant="secondary" disabled={!next} onClick={() => next && setCursors((c) => [...c, next])}>Next</Button>
            </Flex>
          }
        />
      ) : null}
    </Stack>
  );

  const reviewCount = (summary?.by_status.under_review ?? 0) + (summary?.by_status.approved ?? 0);
  return (
    <Stack gap={24}>
      <SectionHeader
        title="Knowledge Base"
        description="Help articles Play users read in the app, and internal playbooks and reply templates for the support team. Everything goes draft → review → approved → published; whoever sends an article for review can't approve it."
        actions={
          <Flex gap={8} wrap>
            <Button onClick={() => setCreateOpen(true)}>Write an article</Button>
            <Button variant="secondary" onClick={() => void navigate("/app/knowledge/policies")}>Policies</Button>
          </Flex>
        }
      />
      {summary ? (
        <Flex gap={12} wrap>
          {(
            [
              ["Live in the app", summary.live_public, () => update({ view: "public" })],
              ["Waiting for review", reviewCount, () => update({ view: "review" })],
              ["Drafts", summary.by_status.draft ?? 0, null],
              ["Review overdue", summary.review_overdue, () => update({ review_overdue: "true", view: view })],
            ] as [string, number, (() => void) | null][]
          ).map(([label, n, go]) => (
            <Card key={label} style={{ minWidth: 150, cursor: go ? "pointer" : undefined }} onClick={go ?? undefined}>
              <Stack gap={4}>
                <Text role="caption" color="secondary" as="span">{label}</Text>
                <Text role="h2" as="span">{n}</Text>
              </Stack>
            </Card>
          ))}
        </Flex>
      ) : null}
      <Filters>
        <Flex gap={12} wrap align="end">
          <Search value={search} onValueChange={setSearch} placeholder="Search titles, summaries and text" label="Search articles" />
          <Select
            label="Category"
            value={category}
            onChange={(e) => update({ category: e.target.value })}
            options={[
              { value: "", label: "All categories" },
              ...Object.entries(summary?.categories ?? {})
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([name, n]) => ({ value: name, label: `${name} (${n})` })),
            ]}
          />
          {overdue ? (
            <Button variant="ghost" onClick={() => update({ review_overdue: "" })}>Showing review overdue · clear</Button>
          ) : null}
        </Flex>
      </Filters>
      <Tabs
        value={view}
        onValueChange={(id) => update({ view: id })}
        items={VIEWS.map((v) => ({
          id: v.id,
          label: v.id === "review" && reviewCount ? `${v.label} (${reviewCount})` : v.label,
          content: table,
        }))}
      />
      <CreateArticleDialog
        open={createOpen}
        defaultAudience={view === "internal" ? "internal" : "public"}
        categories={Object.keys(summary?.categories ?? {})}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => {
          toast.push({ title: "Draft created", tone: "success" });
          void navigate(`/app/knowledge/articles/${id}?edit=1`);
        }}
      />
    </Stack>
  );
}

function CreateArticleDialog({
  open,
  onClose,
  onCreated,
  defaultAudience,
  categories,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
  defaultAudience: ArticleAudience;
  categories: string[];
}) {
  const client = useApiClient();
  const api = useMemo(() => createArticlesApi(client), [client]);
  const [title, setTitle] = useState("");
  const [type, setType] = useState<ArticleType>("help_article");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setTitle("");
      setType(defaultAudience === "public" ? "help_article" : "playbook");
      setCategory("");
      setError(null);
    }
  }, [open, defaultAudience]);
  const audience: ArticleAudience = type === "help_article" ? "public" : "internal";
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Write an article"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            disabled={busy || !title.trim()}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const r = await api.create({
                  title: title.trim(),
                  item_type: type,
                  audience,
                  ...(category.trim() ? { category: category.trim() } : {}),
                });
                onCreated(r.article.id);
              } catch (err) {
                setError(apiErrorMessage(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Creating…" : "Create draft"}
          </Button>
        </>
      }
    >
      <Stack gap={12}>
        <Input label="Title" value={title} maxLength={200} placeholder="e.g. How do I cancel a booking?" onChange={(e) => setTitle(e.target.value)} autoFocus />
        <Select
          label="Kind"
          value={type}
          onChange={(e) => setType(e.target.value as ArticleType)}
          options={(Object.keys(TYPE_LABEL) as ArticleType[]).map((t) => ({ value: t, label: TYPE_LABEL[t] }))}
          hint={audience === "public" ? "Users will be able to read this in the Play app once it is published." : "Staff only. Never shown in the app."}
        />
        <Input label="Category" value={category} maxLength={60} list="kb-categories" placeholder="e.g. Bookings" onChange={(e) => setCategory(e.target.value)} />
        <datalist id="kb-categories">
          {categories.map((c) => <option key={c} value={c} />)}
        </datalist>
        {error ? <Alert tone="danger">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function ArticleDetailPage() {
  const { articleId = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const client = useApiClient();
  const api = useMemo(() => createArticlesApi(client), [client]);
  const [params, setParams] = useSearchParams();
  const editing = params.get("edit") === "1";

  const [detail, setDetail] = useState<ArticleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; status?: number } | null>(null);
  const [action, setAction] = useState<ArticleAction | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [viewVersion, setViewVersion] = useState<{ version: number; title: string | null; body: string | null; change_note: string | null } | null>(null);
  const [tickets, setTickets] = useState<Awaited<ReturnType<typeof api.usage>>["tickets"]>([]);
  const [ticketId, setTicketId] = useState("");

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Knowledge Base", to: "/app/knowledge" },
    { label: detail?.article.title ?? "Article" },
  ]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, u] = await Promise.all([api.get(articleId), api.usage(articleId)]);
      setDetail(d);
      setTickets(u.tickets);
    } catch (err) {
      setError({ message: apiErrorMessage(err), ...(err instanceof ApiError ? { status: err.status } : {}) });
    } finally {
      setLoading(false);
    }
  }, [api, articleId]);

  useEffect(() => {
    void load();
  }, [load]);

  function setEditing(on: boolean) {
    const n = new URLSearchParams(params);
    if (on) n.set("edit", "1");
    else n.delete("edit");
    setParams(n);
  }

  if (loading && !detail) return <LoadingState message="Loading article…" />;
  if (error && !detail) {
    return (
      <EmptyState
        title={error.status === 404 ? "Article not found" : error.status === 403 ? "You don't have access to the Knowledge Base" : "Could not load the article"}
        description={error.message}
        action={<Button variant="secondary" onClick={() => void navigate("/app/knowledge")}>Back to Knowledge Base</Button>}
      />
    );
  }
  if (!detail) return null;
  const a = detail.article;

  async function runAction(act: ArticleAction, text?: string) {
    setBusy(true);
    setDialogError(null);
    try {
      await api.action(articleId, act, text);
      toast.push({ title: `${ACTION_LABEL[act]}: done`, tone: "success" });
      setAction(null);
      setNote("");
      await load();
    } catch (err) {
      setDialogError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (editing && detail.viewer.can_edit) {
    return (
      <ArticleEditor
        article={a}
        onCancel={() => setEditing(false)}
        onSaved={async () => {
          setEditing(false);
          await load();
        }}
      />
    );
  }

  const visibleActions = detail.viewer.actions.filter((x) => !(x === "approve" && detail.viewer.self_approval_blocked));

  return (
    <Stack gap={24}>
      <SectionHeader
        title={a.title ?? "Untitled"}
        description={`${TYPE_LABEL[a.item_type]} · ${a.audience === "public" ? "Users can read this once published" : "Staff only"} · version ${a.version}`}
        actions={
          <Flex gap={8} wrap>
            {detail.viewer.can_edit ? <Button variant="secondary" onClick={() => setEditing(true)}>Edit</Button> : null}
            {visibleActions.map((x) => (
              <Button
                key={x}
                variant={x === "publish" || x === "approve" ? "accent" : x === "archive" || x === "unpublish" ? "danger" : "secondary"}
                disabled={busy}
                onClick={() => {
                  if (NOTE_REQUIRED.includes(x) || NOTE_OPTIONAL.includes(x)) {
                    setNote("");
                    setAction(x);
                  } else void runAction(x);
                }}
              >
                {ACTION_LABEL[x]}
              </Button>
            ))}
            {a.body ? (
              <Button
                variant="ghost"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(a.body ?? "");
                    toast.push({ title: "Copied the article text", tone: "success" });
                  } catch {
                    toast.push({ title: "Clipboard is not available here", tone: "warning" });
                  }
                }}
              >
                Copy text
              </Button>
            ) : null}
          </Flex>
        }
      />

      {detail.viewer.self_approval_blocked ? (
        <Alert tone="info" title="Waiting on someone else">
          You sent this for review, so another person with publishing rights has to approve it.
        </Alert>
      ) : null}
      {a.status === "under_review" && !detail.viewer.can_publish ? (
        <Alert tone="info">In review. Someone with publishing rights (Platform › Configure) approves and publishes it.</Alert>
      ) : null}
      {a.review_overdue ? (
        <Alert tone="warning" title="Review overdue">
          This article was due for a check on {new Date(a.review_due_at!).toLocaleDateString()}. Read it through and update the review date.
        </Alert>
      ) : null}

      <Flex gap={16} wrap align="start">
        <Card style={{ flex: "2 1 520px", minWidth: 0 }} header={<Flex gap={8} align="center"><StatusChip tone={statusTone(a.status)}>{formatLabel(a.status)}</StatusChip>{a.category ? <Text role="small" color="secondary" as="span">{a.category}</Text> : null}</Flex>}>
          <Stack gap={12}>
            {a.summary ? <Text role="body" color="secondary" as="p"><em>{a.summary}</em></Text> : null}
            <Markdown source={a.body ?? ""} />
            {a.tags.length ? (
              <Flex gap={4} wrap>
                {a.tags.map((t) => <StatusChip key={t} tone="neutral">#{t}</StatusChip>)}
              </Flex>
            ) : null}
            {a.content_reference ? (
              <Text role="caption" color="muted" as="p">External reference: {a.content_reference}</Text>
            ) : null}
          </Stack>
        </Card>

        <Stack gap={16} style={{ flex: "1 1 300px", minWidth: 0 }}>
          <Card header={<Text role="title" as="h3">Workflow</Text>}>
            <Stack gap={8}>
              <Meta label="Owner" value={a.owner_name ?? "—"} />
              <Meta label="Written by" value={a.created_by_name ?? "—"} />
              <Meta label="Sent for review by" value={a.submitted_by_name ?? "—"} />
              <Meta label="Approved by" value={a.approved_by_name ?? "—"} />
              <Meta label="Published" value={a.published_at ? `${new Date(a.published_at).toLocaleString()}${a.published_by_name ? ` by ${a.published_by_name}` : ""}` : "—"} />
              <Meta label="Next review" value={a.review_due_at ? new Date(a.review_due_at).toLocaleDateString() : "Not set"} />
            </Stack>
          </Card>

          {a.audience === "public" ? (
            <Card header={<Text role="title" as="h3">Readers</Text>}>
              <Stack gap={8}>
                <Meta label="Views" value={String(a.view_count)} />
                <Meta label="Helpful" value={`${a.helpful} yes · ${a.not_helpful} no`} />
                {detail.feedback_comments.length ? (
                  detail.feedback_comments.map((f, i) => (
                    <Text key={i} role="small" as="p" style={{ borderLeft: "2px solid var(--color-border-subtle)", paddingLeft: 8 }}>
                      {f.helpful ? "👍" : "👎"} {f.comment}
                      <Text role="caption" color="muted" as="span">{` · ${new Date(f.created_at).toLocaleDateString()}`}</Text>
                    </Text>
                  ))
                ) : (
                  <Text role="caption" color="muted" as="p">No written feedback yet.</Text>
                )}
              </Stack>
            </Card>
          ) : null}

          <Card header={<Text role="title" as="h3">Used in tickets ({detail.linked_ticket_count})</Text>}>
            <Stack gap={8}>
              {tickets.map((t) => (
                <Button key={t.support_ticket_id} variant="ghost" size="sm" onClick={() => void navigate(`/app/support/tickets/${t.support_ticket_id}`)}>
                  {t.support_ticket?.public_id ?? "Ticket"} · {formatLabel(t.support_ticket?.status ?? "")}
                </Button>
              ))}
              <Flex gap={8} align="end">
                <Input label="Attach to ticket (ID)" value={ticketId} onChange={(e) => setTicketId(e.target.value)} />
                <Button
                  size="sm"
                  disabled={!/^[0-9a-f-]{36}$/i.test(ticketId.trim())}
                  onClick={async () => {
                    try {
                      const r = await api.linkTicket(articleId, ticketId.trim());
                      toast.push({ title: r.already ? "Already attached" : "Attached to the ticket", tone: "success" });
                      setTicketId("");
                      await load();
                    } catch (err) {
                      toast.push({ title: "Could not attach", description: apiErrorMessage(err), tone: "danger" });
                    }
                  }}
                >
                  Attach
                </Button>
              </Flex>
            </Stack>
          </Card>

          <Card header={<Text role="title" as="h3">History</Text>}>
            <Stack gap={8}>
              {detail.versions.map((v) => (
                <Flex key={v.id} gap={8} justify="space-between" align="center">
                  <Text role="small" as="span">
                    v{v.version} · {v.change_note ?? "Edited"}
                    <Text role="caption" color="muted" as="span">{` · ${v.created_by_name ?? ""} ${new Date(v.created_at).toLocaleDateString()}`}</Text>
                  </Text>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      try {
                        const r = await api.version(articleId, v.version);
                        setViewVersion(r.version);
                      } catch (err) {
                        toast.push({ title: "Could not load that version", description: apiErrorMessage(err), tone: "danger" });
                      }
                    }}
                  >
                    View
                  </Button>
                </Flex>
              ))}
            </Stack>
          </Card>
        </Stack>
      </Flex>

      <Dialog
        open={action !== null}
        onClose={() => {
          setAction(null);
          setDialogError(null);
        }}
        title={action ? ACTION_LABEL[action] : ""}
        actions={
          <>
            <Button variant="secondary" onClick={() => setAction(null)} disabled={busy}>Back</Button>
            <Button
              variant={action === "archive" || action === "unpublish" ? "danger" : "primary"}
              disabled={busy || (action !== null && NOTE_REQUIRED.includes(action) && !note.trim())}
              onClick={() => action && void runAction(action, note.trim() || undefined)}
            >
              {busy ? "Working…" : "Confirm"}
            </Button>
          </>
        }
      >
        <Stack gap={12}>
          {action === "unpublish" ? <Text role="small" as="p">It disappears from the Play app immediately and goes back to draft.</Text> : null}
          <Textarea
            label={action && NOTE_REQUIRED.includes(action) ? "What needs to change" : "Note (optional)"}
            value={note}
            maxLength={1000}
            onChange={(e) => setNote(e.target.value)}
            autoFocus
          />
          {dialogError ? <Alert tone="danger">{dialogError}</Alert> : null}
        </Stack>
      </Dialog>

      <Dialog
        open={viewVersion !== null}
        onClose={() => setViewVersion(null)}
        title={viewVersion ? `Version ${viewVersion.version}${viewVersion.title ? `: ${viewVersion.title}` : ""}` : ""}
        actions={
          viewVersion && detail.viewer.can_edit && viewVersion.version !== a.version ? (
            <Button
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await api.restore(articleId, viewVersion.version, a.version);
                  toast.push({ title: `Restored version ${viewVersion.version} as a new version`, tone: "success" });
                  setViewVersion(null);
                  await load();
                } catch (err) {
                  toast.push({ title: "Could not restore", description: apiErrorMessage(err), tone: "danger" });
                } finally {
                  setBusy(false);
                }
              }}
            >
              Restore this version
            </Button>
          ) : undefined
        }
      >
        {viewVersion ? <Markdown source={viewVersion.body ?? ""} /> : null}
      </Dialog>
    </Stack>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <Flex gap={8} justify="space-between">
      <Text role="small" color="secondary" as="span">{label}</Text>
      <Text role="small" as="span" style={{ textAlign: "right" }}>{value}</Text>
    </Flex>
  );
}

function ArticleEditor({ article, onCancel, onSaved }: { article: ArticleDto; onCancel: () => void; onSaved: () => void | Promise<void> }) {
  const client = useApiClient();
  const api = useMemo(() => createArticlesApi(client), [client]);
  const toast = useToast();
  const [title, setTitle] = useState(article.title ?? "");
  const [summary, setSummary] = useState(article.summary ?? "");
  const [body, setBody] = useState(article.body ?? "");
  const [category, setCategory] = useState(article.category ?? "");
  const [tags, setTags] = useState(article.tags.join(", "));
  const [type, setType] = useState<ArticleType>(article.item_type);
  const [reviewDue, setReviewDue] = useState(article.review_due_at ? article.review_due_at.slice(0, 10) : "");
  const [changeNote, setChangeNote] = useState("");
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const live = article.status === "published";
  const reviewed = article.status === "under_review" || article.status === "approved";
  const dirty =
    title !== (article.title ?? "") ||
    summary !== (article.summary ?? "") ||
    body !== (article.body ?? "") ||
    category !== (article.category ?? "") ||
    tags !== article.tags.join(", ") ||
    type !== article.item_type ||
    reviewDue !== (article.review_due_at ? article.review_due_at.slice(0, 10) : "");

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const audience: ArticleAudience = type === "help_article" ? "public" : "internal";
      await api.update(article.id, {
        expected_version: article.version,
        ...(changeNote.trim() ? { change_note: changeNote.trim() } : {}),
        title: title.trim(),
        summary: summary.trim() ? summary.trim() : null,
        body,
        category: category.trim() ? category.trim() : null,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        review_due_at: reviewDue ? new Date(`${reviewDue}T00:00:00`).toISOString() : null,
        ...(type !== article.item_type ? { item_type: type } : {}),
        ...(audience !== article.audience ? { audience } : {}),
      });
      toast.push({ title: "Saved", tone: "success" });
      await onSaved();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap={16}>
      <SectionHeader
        title={`Editing: ${article.title ?? "Untitled"}`}
        description={
          live
            ? "This article is live. Your changes appear in the app as soon as you save."
            : reviewed
              ? "Saving a change sends this back to draft; it will need review again."
              : "Draft. Nobody outside HQ can see it."
        }
        actions={
          <Flex gap={8}>
            <Button variant="secondary" onClick={() => (dirty && !window.confirm("Discard your changes?") ? undefined : onCancel())} disabled={busy}>Cancel</Button>
            <Button onClick={() => void save()} disabled={busy || !title.trim() || !dirty}>{busy ? "Saving…" : "Save"}</Button>
          </Flex>
        }
      />
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <Flex gap={16} wrap align="start">
        <Stack gap={12} style={{ flex: "2 1 520px", minWidth: 0 }}>
          <Input label="Title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
          <Input
            label="Summary"
            hint={type === "help_article" ? "Required to publish. Users see it in the list." : "One line shown in the list."}
            value={summary}
            maxLength={400}
            onChange={(e) => setSummary(e.target.value)}
          />
          <Flex gap={8}>
            <Button size="sm" variant={preview ? "secondary" : "primary"} onClick={() => setPreview(false)}>Write</Button>
            <Button size="sm" variant={preview ? "primary" : "secondary"} onClick={() => setPreview(true)}>Preview</Button>
          </Flex>
          {preview ? (
            <Card><Markdown source={body} /></Card>
          ) : (
            <Textarea
              label="Article"
              hint="Markdown: # Heading, - bullet, 1. step, **bold**, *italic*, [link](https://…)"
              rows={22}
              value={body}
              maxLength={60000}
              onChange={(e) => setBody(e.target.value)}
              style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
            />
          )}
        </Stack>
        <Stack gap={12} style={{ flex: "1 1 280px", minWidth: 0 }}>
          <Select
            label="Kind"
            value={type}
            disabled={live}
            hint={live ? "Unpublish before changing who can read it." : type === "help_article" ? "Readable by Play users once published." : "Staff only."}
            onChange={(e) => setType(e.target.value as ArticleType)}
            options={(Object.keys(TYPE_LABEL) as ArticleType[]).map((t) => ({ value: t, label: TYPE_LABEL[t] }))}
          />
          <Input label="Category" value={category} maxLength={60} onChange={(e) => setCategory(e.target.value)} />
          <Input label="Tags" hint="Comma separated" value={tags} onChange={(e) => setTags(e.target.value)} />
          <Input type="date" label="Next review" hint="When someone should check this is still right." value={reviewDue} onChange={(e) => setReviewDue(e.target.value)} />
          <Input label="What changed (optional)" value={changeNote} maxLength={300} onChange={(e) => setChangeNote(e.target.value)} />
        </Stack>
      </Flex>
    </Stack>
  );
}
